import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AppService } from 'src/app/app.service';

/** 商戶要求付款前須驗證手機，會員尚未綁定手機（ALL-14317） */
export const PHONE_2FA_REQUIRED_CODE = '2145';

/**
 * 付款前 Phone 2FA 限制（ALL-12362 R012）。
 *
 * 前端依 getuserinfo 的 isNeedMobileVerify 直接在錢包擋下存提款；
 * 2145 是後端防止繞過前端直接打 API 的保護，前端收到時同樣顯示阻擋提示。
 */
@Injectable({ providedIn: 'root' })
export class PaymentPhoneVerificationService {
  constructor(
    private readonly appService: AppService,
    private readonly router: Router,
  ) {}

  /** 是否收到後端 2145（例如會員資料尚未更新時） */
  public readonly blocked = signal(false);

  /** 由付款提示進入設定時記住的付款頁面；離開設定即清除 */
  private returnUrl: string | null = null;

  /** Security 頁面開啟後是否要直接開啟手機設定（只使用一次） */
  private pendingSetupOpen = false;

  /** 已在 Security 頁面時不會重新初始化，改由此通知開啟手機設定 */
  public readonly setupRequested$ = new Subject<void>();

  public isRequired(): boolean {
    return this.appService.userInfo$.value?.isNeedMobileVerify === true || this.blocked();
  }

  public markBlocked(): void {
    this.blocked.set(true);
  }

  public reset(): void {
    this.blocked.set(false);
  }

  /**
   * 從付款提示開啟 Security 的手機設定，並記住目前的付款頁面。
   *
   * @param returnUrl 付款頁面網址（需包含錢包的 query，返回時才會重新開啟錢包）
   */
  public startSetup(returnUrl: string): Promise<boolean> {
    this.returnUrl = returnUrl;
    this.pendingSetupOpen = true;
    return this.router.navigate([this.appService.languageCode, 'settings', 'security']).then(navigated => {
      this.setupRequested$.next();
      return navigated;
    });
  }

  /** Security 頁面初始化時呼叫：由付款提示進入時回傳 true，讓頁面直接開啟手機設定 */
  public consumeSetupRequest(): boolean {
    const shouldOpen = this.pendingSetupOpen;
    this.pendingSetupOpen = false;
    return shouldOpen;
  }

  /** 是否由付款提示進入、且尚未離開手機設定 */
  public get hasActiveReturn(): boolean {
    return !!this.returnUrl;
  }

  /** 手機設定成功：返回付款頁面（不自動送出付款，由付款頁面重新檢查資格） */
  public completeSetup(): void {
    const returnUrl = this.returnUrl;
    this.returnUrl = null;
    this.blocked.set(false);
    if (returnUrl) {
      void this.router.navigateByUrl(returnUrl);
    }
  }

  /** 未完成就離開手機設定：取消返回 */
  public cancelReturn(): void {
    this.returnUrl = null;
  }
}
